/**
 * SHO-10301 — Offerte-knop op de PDP naar GA4 via de Custom Pixel.
 *
 * De sales-app (`offerte-button.js`) pusht `quote_pdp_*` op storefront-`dataLayer`.
 * GTM draait in de pixel-sandbox en ziet die pushes niet. Deze bridge vertaalt
 * ze naar `Shopify.analytics.publish('hds:offerte', …)`.
 *
 * | App-event                 | action     |
 * | quote_pdp_button_view     | view       |
 * | quote_pdp_add_success     | click      |
 * | quote_pdp_add_error       | add_error  |
 *
 * Keten: publish `hds:offerte` → Custom Pixel → dataLayer `hds_offerte` → GTM → GA4.
 * ⚠️ De pixel filtert op `action`. Een vierde action vraagt óók pixel- en GTM-wijziging.
 *
 * Elke payload draagt de paginacontext (`page_location`, `page_path`, `page_title`) mee,
 * net als de andere bridges. Zonder die velden valt de pixel terug op de URL van zijn
 * eigen sandbox-iframe (`…/web-pixels@…/sandbox/…`) en belandt die in GA4 als pagina.
 *
 * `error` gaat alleen mee bij `add_error`, en alleen als whitelist
 * (`no_variant_selected` | `add_failed`) of anders `unknown`.
 */

(function hdsOfferteAnalytics() {
  if (window.__hdsOfferteAnalyticsLoaded) return;
  window.__hdsOfferteAnalyticsLoaded = true;

  const PUBLISH_EVENT = 'hds:offerte';
  const DEADLINE_MS = 8000;
  const RETRY_MS = 250;

  /** @type {Record<string, string>} */
  const ACTION_BY_EVENT = {
    quote_pdp_button_view: 'view',
    quote_pdp_add_success: 'click',
    quote_pdp_add_error: 'add_error',
  };

  const ERROR_WHITELIST = {
    no_variant_selected: true,
    add_failed: true,
  };

  let viewPublished = false;

  /**
   * @param {Record<string, unknown>} payload
   */
  function publish(payload) {
    const deadline = Date.now() + DEADLINE_MS;

    const tick = () => {
      try {
        const publishFn = window.Shopify?.analytics?.publish;

        if (typeof publishFn === 'function') {
          publishFn(PUBLISH_EVENT, payload);
          return;
        }
      } catch {
        return;
      }

      if (Date.now() < deadline) setTimeout(tick, RETRY_MS);
    };

    tick();
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  function asId(value) {
    if (value == null || value === '') return '';

    return String(value);
  }

  /**
   * @param {unknown} value
   * @returns {string}
   */
  function sanitizeError(value) {
    const raw = String(value || '').trim();

    if (!raw) return '';
    if (ERROR_WHITELIST[raw]) return raw;

    return 'unknown';
  }

  /**
   * Paginacontext van de storefront, zodat de pixel niet zijn sandbox-URL doorgeeft.
   *
   * @returns {Record<string, string>}
   */
  function pageContext() {
    /** @type {Record<string, string>} */
    const context = {};

    try {
      if (window.location?.href) context.page_location = String(window.location.href);
      if (window.location?.pathname) context.page_path = String(window.location.pathname);
      if (document.title) context.page_title = String(document.title);
    } catch {
      // zonder context liever een kale payload dan geen event
    }

    return context;
  }

  /**
   * @param {string} action
   * @param {Record<string, unknown>} data
   * @returns {Record<string, unknown>}
   */
  function buildPayload(action, data) {
    /** @type {Record<string, unknown>} */
    const payload = { action, ...pageContext() };
    const productId = asId(data.product_id ?? data.productId);

    if (productId) payload.product_id = productId;

    const variantId = asId(data.variant_id ?? data.variantId);

    if (variantId) payload.variant_id = variantId;

    if (action === 'add_error') {
      const error = sanitizeError(data.error);

      if (error) payload.error = error;
    }

    return payload;
  }

  /**
   * @param {unknown} item
   */
  function handleDataLayerItem(item) {
    if (!item || typeof item !== 'object') return;

    const eventName = /** @type {{ event?: unknown }} */ (item).event;
    const action = typeof eventName === 'string' ? ACTION_BY_EVENT[eventName] : '';

    if (!action) return;

    if (action === 'view') {
      if (viewPublished) return;
      viewPublished = true;
    }

    publish(buildPayload(action, /** @type {Record<string, unknown>} */ (item)));
  }

  function hookDataLayer() {
    const existing = window.dataLayer;
    const queue = Array.isArray(existing) ? existing : [];

    window.dataLayer = queue;

    if (queue.__hdsOfferteWrapped) return;

    queue.__hdsOfferteWrapped = true;

    const originalPush = queue.push.bind(queue);

    queue.push = function hdsOfferteDataLayerPush(...args) {
      const result = originalPush(...args);

      for (const arg of args) {
        try {
          handleDataLayerItem(arg);
        } catch {
          // meten mag winkelen nooit breken
        }
      }

      return result;
    };

    for (const item of queue) {
      try {
        handleDataLayerItem(item);
      } catch {
        // meten mag winkelen nooit breken
      }
    }
  }

  function fallbackView() {
    if (viewPublished) return;

    const button = document.getElementById('offerte-button');

    if (!button) return;

    const root = document.getElementById('pdp-quote-button-root');

    viewPublished = true;
    publish(
      buildPayload('view', {
        productId: root && root.dataset ? root.dataset.productId : '',
      }),
    );
  }

  try {
    hookDataLayer();

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fallbackView);
    } else {
      fallbackView();
    }
  } catch {
    // meten mag winkelen nooit breken
  }
})();
